import features from '@dashboard/i18n/utils/features';
import formats from '@dashboard/i18n/utils/formats';
import { extend } from '@square/dashboard-utilities/utils/keys';
export function loadFormats(browserReportedLanguage, countryCode) {
  // start with all en-US formats.
  const args = [formats.default];

  // there are some browser languages that include a country code, e.g. Chrome has an option
  // for "English (Australia)", which means that `browserReportedLanguage` will be `en-AU`.
  const splitBrowserReportedLanguage = browserReportedLanguage.split('-');
  const browserReportedLanguageIncludesCountryCode = splitBrowserReportedLanguage.length > 1;

  // a browser-reported lang+country should have precedence over `countryCode` - in other words,
  // if your browser language is "English (Australia)", but your shop is physically located in the US,
  // you should still see Australian localization of things like times and dates.
  if (browserReportedLanguageIncludesCountryCode) {
    args.push(
    // layer on all formats based on just the languloadFeatures age component.
    formats[splitBrowserReportedLanguage[0]],
    // layer on all formats based on both the language and country components.
    formats[browserReportedLanguage]);
  } else {
    args.push(
    // layer on all formats based on just the browser language.
    formats[browserReportedLanguage],
    // layer on all formats based on the browser language plus the merchant's physical country code.
    // this is needed for places like Ireland, where there isn't an "English (Ireland)" browser language,
    // but if your store is physically there, you probably still want to see the formats for Ireland.
    formats[`${browserReportedLanguage}-${countryCode}`]);
  }
  const fallbacks = formats.default.date.fallbacks;
  const argsWithFallbacks = args.filter(Boolean).map(format => withFallbacks(format, fallbacks));
  return extend(true, {}, ...argsWithFallbacks);
}

// This function generates a feature set for a country by layering the country-specific
// features on top of a default feature set.
function _loadFeatures(countryCode) {
  // Timezones are handled specially by combining the default timezones and the country specific timezones.
  // If there is a country specific timezone list, a new timezone list is generated by appending the
  // default list to the country specific list and removing duplicates.
  // See https://jira.sqprod.co/browse/SELPRO-3947
  if (features.default?.timezoneSelections && features[countryCode]?.timezoneSelections) {
    const timezoneValueSet = new Set();
    const timezoneSelections = [...features[countryCode].timezoneSelections, ...features.default.timezoneSelections].filter(function (timezoneSelection) {
      if (timezoneValueSet.has(timezoneSelection.value)) {
        return false;
      }
      timezoneValueSet.add(timezoneSelection.value);
      return true;
    });
    return extend(true, {}, features.default, features[countryCode], {
      timezoneSelections
    });
  }
  return extend(true, {}, features.default, features[countryCode]);
}

/**
 * Fallbacks (defined in "default") give what other string we should
 * fall back to if something isn't defined in a particular locale.
 * Unlike the normal default overrides, fallbacks use a different format string
 * of that same locale, so a locale that doesn't do anything for the `withoutMeridiem`
 * strings doesn't need to repeat what they did for the ordinary strings, as they should
 * always fall back to the "with meridiem" versions.
 */
export function withFallbacks(formatObj, fallbacks) {
  const originalFormat = formatObj?.date?.format;
  if (!originalFormat) {
    return formatObj;
  }
  const format = {
    ...originalFormat
  };
  for (const [formatName, possibleFallbacks] of Object.entries(fallbacks)) {
    if (!format[formatName]) {
      const fallbacksArray = Array.isArray(possibleFallbacks) ? possibleFallbacks : [possibleFallbacks];
      for (const fallback of fallbacksArray) {
        if (originalFormat[fallback]) {
          format[formatName] = originalFormat[fallback];
          break;
        }
      }
    }
  }
  return {
    ...formatObj,
    date: {
      ...formatObj.date,
      format
    }
  };
}
export const i18nUtilsMocks = {
  loadFeatures(countryCode) {
    return _loadFeatures(countryCode);
  }
};
export function loadFeatures(countryCode) {
  return i18nUtilsMocks.loadFeatures(countryCode);
}